import React from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import Pressable from '@atlaskit/primitives/pressable';
import Textfield from '@atlaskit/textfield';
import SelectClearIcon from '@atlaskit/icon/core/cross-circle';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/new';
import { Inline } from '@atlaskit/primitives/compiled';
import { Stack } from '@atlaskit/primitives';
import PersonRemoveIcon from '@atlaskit/icon/core/person-remove';

export enum SpecificAccessFilterType {
	ALL = 'all',
	USERS = 'users',
	TEAMS = 'teams',
	GUESTS = 'guests',
}

/**
 * All available access type filter options
 */
export const SPECIFIC_ACCESS_FILTER_OPTIONS: SpecificAccessFilterType[] = [
	SpecificAccessFilterType.ALL,
	SpecificAccessFilterType.USERS,
	SpecificAccessFilterType.TEAMS,
	SpecificAccessFilterType.GUESTS,
];

export type SpecificAccessFilterValue = SpecificAccessFilterType;

export type SpecificAccessFilterProps = {
	setFilterText: (textValue: string) => void;
	filterText: string;
	countOfIndividuals: number;
	countOfTeams: number;
	countOfGroups: number;
	selectedAccessType: SpecificAccessFilterValue;
	onAccessTypeChange: (accessType: SpecificAccessFilterValue) => void;
	onOpenRemoveAllConfirm: () => void;
};

const styles = cssMap({
	filterStyle: { marginBottom: token('space.200') },
	pressable: {
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

const i18n = defineMessages({
	clearFilterLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.clear-filter',
		defaultMessage: 'Clear selection',
		description: 'Label for button that clears text from the filter field',
	},
	countOfIndividualsSuffix: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-groups-and-individuals-suffix',
		defaultMessage: 'and {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description:
			'Latter part of placeholder text when there are both individuals and groups, to be concatenated with share-and-restrict-dialog.specific-access-dialog.count-of-groups',
	},
	countOfGroups: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-groups',
		defaultMessage: 'Search {countOfGroups, plural, one {# group} other {# groups}}',
		description: 'Placeholder text when there are only groups',
	},
	countOfIndividuals: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-individuals',
		defaultMessage: 'Search {countOfIndividuals, plural, one {# individual} other {# individuals}}',
		description: 'Placeholder text when there are only individuals',
	},
	countOfTeamsMiddleClause: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams-middle-clause',
		defaultMessage: '{countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the content',
	},
	countOfTeams: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams',
		defaultMessage: 'Search {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are only teams in the content',
	},
	countOfTeamsSuffix: {
		id: 'share-and-restrict-dialog.specific-access-dialog.count-of-teams-suffix',
		defaultMessage: 'and {countOfTeams, plural, one {# team} other {# teams}}',
		description: 'Placeholder text when there are teams in the content',
	},
	allLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.filter-all-label',
		defaultMessage: 'All',
		description: 'Label for filter button to show all principal types',
	},
	usersLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.filter-users-label',
		defaultMessage: 'Users',
		description: 'Label for filter button to show only user principals',
	},
	teamsLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.filter-teams-label',
		defaultMessage: 'Teams',
		description: 'Label for filter button to show only team and group principals',
	},
	guestsLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.filter-guests-label',
		defaultMessage: 'Guests',
		description: 'Label for filter button to show only external user principals',
	},
	removeAllLabel: {
		id: 'share-and-restrict-dialog.specific-access-dialog.remove-all-label',
		defaultMessage: 'Remove all',
		description: 'Label for remove all button to remove all principals',
	},
});

type PlaceholderParams = {
	countOfIndividuals: number;
	countOfGroups: number;
	countOfTeams: number;
};

export const SpecificAccessFilter = ({
	setFilterText,
	filterText,
	countOfIndividuals,
	countOfGroups,
	countOfTeams,
	selectedAccessType,
	onAccessTypeChange,
	onOpenRemoveAllConfirm,
}: SpecificAccessFilterProps) => {
	const intl = useIntl();
	const handleFilterTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setFilterText(event.currentTarget.value);
	};

	const placeholder = getFilterPlaceholderText(
		{ countOfIndividuals, countOfGroups, countOfTeams },
		intl,
	);

	return (
		<Stack space="space.200" xcss={styles.filterStyle}>
			<Textfield
				aria-label={placeholder}
				autoFocus
				placeholder={placeholder}
				value={filterText}
				onChange={handleFilterTextChange}
				elemAfterInput={
					<>
						{Boolean(filterText) && (
							<Pressable
								aria-label={intl.formatMessage(i18n.clearFilterLabel)}
								onClick={() => setFilterText('')}
								xcss={styles.pressable}
							>
								<SelectClearIcon label="" color={token('color.icon.subtle')} />
							</Pressable>
						)}
					</>
				}
			/>
			<Inline alignBlock="center" spread="space-between">
				<AccessTypeSelector
					selectedAccessType={selectedAccessType}
					onAccessTypeChange={onAccessTypeChange}
				/>
				<Button
					spacing="compact"
					appearance="subtle"
					iconBefore={PersonRemoveIcon}
					onClick={onOpenRemoveAllConfirm}
				>
					{intl.formatMessage(i18n.removeAllLabel)}
				</Button>
			</Inline>
		</Stack>
	);
};

export type AccessTypeSelectorProps = {
	selectedAccessType: SpecificAccessFilterValue;
	onAccessTypeChange: (accessType: SpecificAccessFilterValue) => void;
};

const FILTER_SELECTIONS = [
	{ id: SpecificAccessFilterType.ALL, labelKey: i18n.allLabel },
	{ id: SpecificAccessFilterType.USERS, labelKey: i18n.usersLabel },
	{ id: SpecificAccessFilterType.TEAMS, labelKey: i18n.teamsLabel },
	{ id: SpecificAccessFilterType.GUESTS, labelKey: i18n.guestsLabel },
] as const;

export const AccessTypeSelector = ({
	selectedAccessType,
	onAccessTypeChange,
}: AccessTypeSelectorProps) => {
	const intl = useIntl();

	return (
		<Inline space="space.100">
			{FILTER_SELECTIONS.map((selection) => (
				<Button
					key={selection.id}
					isSelected={selectedAccessType === selection.id}
					onClick={() => onAccessTypeChange(selection.id)}
				>
					{intl.formatMessage(selection.labelKey)}
				</Button>
			))}
		</Inline>
	);
};

function getFilterPlaceholderText(
	{ countOfIndividuals, countOfGroups, countOfTeams }: PlaceholderParams,
	intl: ReturnType<typeof useIntl>,
): string {
	if (countOfIndividuals && countOfGroups && countOfTeams) {
		return `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})}, ${intl.formatMessage(i18n.countOfTeamsMiddleClause, { countOfTeams })}, ${intl.formatMessage(
			i18n.countOfIndividualsSuffix,
			{
				countOfIndividuals,
			},
		)}`;
	}

	if (countOfGroups && countOfIndividuals) {
		return `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
			countOfIndividuals,
		})}`;
	}

	if (countOfIndividuals && countOfTeams) {
		return `${intl.formatMessage(i18n.countOfTeams, {
			countOfTeams,
		})} ${intl.formatMessage(i18n.countOfIndividualsSuffix, {
			countOfIndividuals,
		})}`;
	}

	if (countOfTeams && countOfGroups) {
		return `${intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		})} ${intl.formatMessage(i18n.countOfTeamsSuffix, {
			countOfTeams,
		})}`;
	}

	if (countOfTeams) {
		return intl.formatMessage(i18n.countOfTeams, {
			countOfTeams,
		});
	}

	if (countOfGroups) {
		return intl.formatMessage(i18n.countOfGroups, {
			countOfGroups,
		});
	}

	return intl.formatMessage(i18n.countOfIndividuals, {
		countOfIndividuals,
	});
}
